import { useFormik } from "formik";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory, useParams, withRouter } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { object, string } from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useArrondissement,
  useCities,
  useCountries,
  useProvinces,
  useUpdateArrondissement,
} from "../../../hooks/useGeography";

const UpdateArrondissement = (props) => {
  const history = useHistory();
  const { id } = useParams();
  const arrondissementQ = useArrondissement(id);
  const updateArrondissementMutation = useUpdateArrondissement();
  const countriesQ = useCountries();
  const provincesQ = useProvinces(arrondissementQ.data?.country);
  const citiesQ = useCities(arrondissementQ.data?.province);

  document.title = props.t(
    "Modification d'un arrondissement - Le Conseiller du BTP"
  );

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: arrondissementQ.data?.name || "",
      country: arrondissementQ.data?.country || "",
      province: arrondissementQ.data?.province || "",
      city: arrondissementQ.data?.city || "",
    },

    validationSchema: object().shape({
      name: string().required(
        props.t("Veuillez entrer le nom de l'arrondissement")
      ),
    }),

    onSubmit: async (values) => {
      updateArrondissementMutation.mutate(
        {
          id: arrondissementQ.data?.id,
          name: values.name,
        },
        {
          onSuccess: () => {
            history.push("/geolocalisation-arrondissement");
          },
        }
      );
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Modification d'un arrondissement")}
          pageTitle={props.t("Arrondissement")}
        />

        <Row>
          <Col lg={12}>
            <Form
              encType="multipart/form-data"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Card>
                <CardBody>
                  {arrondissementQ.isLoading ? (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>{props.t("Chargement")}...</h5>
                      </div>
                    </div>
                  ) : arrondissementQ.error ? (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>
                          {arrondissementQ.error.message ||
                            props.t("Une erreur est survenue")}
                        </h5>
                      </div>
                    </div>
                  ) : (
                    <>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="pays">
                      {props.t("Pays")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="country"
                      value={validation.values.country}
                      disabled
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    >
                      <option value="">
                        {props.t("Sélectionner un pays")}
                      </option>
                      {(countriesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.country &&
                      validation.touched.country && (
                        <FormFeedback type="invalid">
                          {validation.errors.country}
                        </FormFeedback>
                      )}
                  </div>

                  {/* Sélection de la province */}
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="province">
                      {props.t("Province")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="province"
                      value={validation.values.province}
                      disabled
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    >
                      <option value="">
                        {props.t("Sélectionner une province")}
                      </option>
                      {(provincesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.province &&
                      validation.touched.province && (
                        <FormFeedback type="invalid">
                          {validation.errors.province}
                        </FormFeedback>
                      )}
                  </div>

                  <div className="mb-3">
                    <Label className="form-label" htmlFor="city">
                      {props.t("Ville")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="city"
                      value={validation.values.city}
                      disabled
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      required
                    >
                      <option value="">
                        {props.t("Sélectionner une ville")}
                      </option>
                      {(citiesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.city && validation.touched.city ? (
                      <FormFeedback type="invalid">
                        {validation.errors.city}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="name">
                      {props.t("Nom de l'arrondissement")}
                    </Label>
                    <Input
                      type="text"
                      className="form"
                      placeholder={props.t("Entrer le nom de l'arrondissement")}
                      name="name"
                      value={validation.values.name}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      required
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>
                    </>
                  )}
                </CardBody>
              </Card>
              <div className="text-end mb-3">
                <Link to="/geolocalisation-arrondissement" className="btn btn-primary me-2">
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning w-sm m-2"
                  disabled={arrondissementQ.isLoading || updateArrondissementMutation.isPending}
                >
                  {props.t("Valider")}
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

UpdateArrondissement.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(UpdateArrondissement));
